// @ts-nocheck
// ============================================================
// AUTONOMOUS COPILOT — Fase 10.10
// Copilot contextual do digital twin — recomendações autônomas
// On-device, sem API externa
// ============================================================
import type {
  TwinState, OperationalRecommendation, ObraZone, SpatialRisk, EventCorrelation,
} from '@/types/twin'
import type { Equipment } from '@/types/twin'
import { computeEquipmentHealth } from '@/core/digital-twin/equipmentTracker'

export interface TwinCopilotQuery {
  question:  string
  obraId:    string
  twinState: TwinState
}

export interface TwinCopilotResponse {
  answer:      string
  intent:      string
  confidence:  number
  recommendations: OperationalRecommendation[]
  zones?:      string[]
}

// ─── Intent detection ─────────────────────────────────────────
const INTENTS: Array<{ id: string; patterns: RegExp[] }> = [
  { id: 'area_critica',   patterns: [/área\s*(mais)?\s*crítica/i, /zona\s*crítica/i, /pior\s*área/i] },
  { id: 'equipe_reforco', patterns: [/reforçar?\s*equipe/i, /onde.*reforçar?/i, /equipe.*insuf/i] },
  { id: 'maior_risco',    patterns: [/maior\s*risco/i, /mais\s*perig/i, /principal\s*risco/i] },
  { id: 'concretagem',    patterns: [/concretagem/i, /concreto.*risco/i, /mais.*vulneráv/i] },
  { id: 'equipamento',    patterns: [/equipamento/i, /grua/i, /elevador/i, /falha.*equip/i] },
  { id: 'logistica',      patterns: [/logística/i, /rota/i, /congestion/i, /bloqueio/i] },
  { id: 'simulacao',      patterns: [/simula/i, /e\s*se\s*/i, /impacto.*atraso/i, /cenário/i] },
  { id: 'resumo',         patterns: [/resumo/i, /situação\s*geral/i, /como\s*está/i, /overview/i] },
]

function detectIntent(q: string): string {
  for (const { id, patterns } of INTENTS) {
    if (patterns.some(p => p.test(q))) return id
  }
  return 'resumo'
}

// ─── Main copilot ─────────────────────────────────────────────
export function askTwinCopilot(query: TwinCopilotQuery): TwinCopilotResponse {
  const { question, twinState } = query
  const intent = detectIntent(question)

  switch (intent) {
    case 'area_critica':   return handleAreaCritica(twinState)
    case 'equipe_reforco': return handleEquipeReforco(twinState)
    case 'maior_risco':    return handleMaiorRisco(twinState)
    case 'concretagem':    return handleConcretagem(twinState)
    case 'equipamento':    return handleEquipamento(twinState)
    case 'logistica':      return handleLogistica(twinState)
    case 'simulacao':      return handleSimulacao(twinState, question)
    default:               return handleResumo(twinState)
  }
}

// ─── Intent handlers ──────────────────────────────────────────
function handleAreaCritica(state: TwinState): TwinCopilotResponse {
  const criticalZones = state.zones
    .filter(z => z.risk_level === 'critical' || z.risk_level === 'high')
    .sort((a, b) => {
      const order = { critical: 3, high: 2, moderate: 1, low: 0 }
      return (order[b.risk_level] ?? 0) - (order[a.risk_level] ?? 0)
    })

  if (criticalZones.length === 0) {
    return respond('area_critica', 'Nenhuma área em estado crítico no momento. Operação dentro dos parâmetros.', 0.9, [])
  }

  const top = criticalZones.slice(0, 3)
  const zoneRisks = top.map(z => {
    const risks = state.zoneRiskMap.get(z.id) ?? []
    const topRisk = risks[0]
    return `• ${z.nome} (${z.risk_level}): ${topRisk?.title ?? 'risco ativo'}`
  })

  return respond(
    'area_critica',
    `As ${top.length} áreas mais críticas são:\n${zoneRisks.join('\n')}\n\nPriorize inspeção em ${top[0]?.nome ?? 'área crítica'}.`,
    0.87,
    top.map(z => buildZoneRec(z, state.zoneRiskMap.get(z.id) ?? [])),
    top.map(z => z.nome),
  )
}

function handleEquipeReforco(state: TwinState): TwinCopilotResponse {
  const productionRisks = state.risks.filter(r => r.risk_type === 'production' && r.is_active && r.score > 0.4)
  const affectedZones = [...new Set(productionRisks.map(r => r.zone_id).filter(Boolean) as string[])]
    .map(id => state.zones.find(z => z.id === id))
    .filter(Boolean) as ObraZone[]

  if (affectedZones.length === 0) {
    return respond('equipe_reforco', 'Não há indicação de necessidade urgente de reforço de equipe no momento.', 0.75, [])
  }

  const zones = affectedZones.slice(0, 2).map(z => z.nome).join(' e ')
  return respond(
    'equipe_reforco',
    `Reforce equipe nas áreas: ${zones}.\n\nHá risco de produção abaixo do esperado nestas zonas. Redistribuição de efetivo pode mitigar atrasos.`,
    0.80,
    [{
      id:         `rec_workforce_${Date.now()}`,
      category:   'workforce',
      priority:   2,
      title:      `Reforço de equipe: ${zones}`,
      rationale:  'Produção abaixo do esperado nas zonas identificadas',
      action:     `Realocar ou contratar mão de obra adicional para ${zones}`,
      impact:     'Redução de risco de atraso em ~2-4 dias',
      confidence: 0.78,
      timeframe:  'today',
      expires_at: new Date(Date.now() + 86_400_000).toISOString(),
    }],
    affectedZones.map(z => z.nome),
  )
}

function handleMaiorRisco(state: TwinState): TwinCopilotResponse {
  const allRisks = state.risks.filter(r => r.is_active).sort((a, b) => b.score - a.score)
  if (allRisks.length === 0) {
    return respond('maior_risco', 'Nenhum risco ativo identificado. Operação estável.', 0.9, [])
  }

  const top = allRisks.slice(0, 3)
  const bullets = top.map(r => `• [${r.severity.toUpperCase()}] ${r.title} (score: ${Math.round(r.score * 100)})`)

  // Check correlations for amplification
  const amplified = state.correlations.filter(c => c.risk_amplifier > 1.5)
  const amplNote = amplified.length > 0
    ? `\n\n⚡ Atenção: ${amplified[0]?.title} (amplificador: ×${amplified[0]?.risk_amplifier.toFixed(1)})`
    : ''

  return respond(
    'maior_risco',
    `Riscos mais críticos no momento:\n${bullets.join('\n')}${amplNote}`,
    0.88,
    [],
  )
}

function handleConcretagem(state: TwinState): TwinCopilotResponse {
  const supplyRisks  = state.risks.filter(r => r.risk_type === 'supply' && r.is_active)
  const weatherRisks = state.risks.filter(r => r.risk_type === 'weather' && r.is_active)
  const correlationWarning = state.correlations.find(c =>
    c.event_types.includes('supply') && c.event_types.includes('weather')
  )

  if (supplyRisks.length === 0 && weatherRisks.length === 0) {
    return respond('concretagem', 'Condições favoráveis para concretagem. Sem riscos de supply ou clima identificados.', 0.85, [])
  }

  const parts: string[] = []
  if (supplyRisks.length > 0) parts.push(`supply crítico em ${supplyRisks.length} área(s)`)
  if (weatherRisks.length > 0) parts.push(`risco climático ativo`)
  if (correlationWarning) parts.push(`risco composto detectado (×${correlationWarning.risk_amplifier.toFixed(1)})`)

  return respond(
    'concretagem',
    `⚠ A concretagem está vulnerável: ${parts.join(', ')}.\n\nRecomenda-se verificar janela climática e disponibilidade de insumos antes de confirmar a concretagem.`,
    0.83,
    [],
  )
}

function handleEquipamento(state: TwinState): TwinCopilotResponse {
  const critical = state.equipment.filter(e => {
    const h = computeEquipmentHealth(e)
    return h.status === 'critical' || h.status === 'offline'
  })

  if (critical.length === 0) {
    return respond('equipamento', 'Todos os equipamentos dentro dos parâmetros operacionais.', 0.9, [])
  }

  const names = critical.map(e => `${e.nome} (${e.status})`).join(', ')
  return respond(
    'equipamento',
    `${critical.length} equipamento(s) em estado crítico: ${names}.\n\nAcione equipe de manutenção imediatamente para ${critical[0]?.nome}.`,
    0.88,
    [],
  )
}

function handleLogistica(state: TwinState): TwinCopilotResponse {
  const logisticsRisks = state.risks.filter(r => r.risk_type === 'logistics' && r.is_active)
  const blocked = logisticsRisks.filter(r => r.score > 0.7)

  if (logisticsRisks.length === 0) {
    return respond('logistica', 'Fluxo logístico normal. Sem bloqueios ou congestionamentos ativos.', 0.9, [])
  }

  return respond(
    'logistica',
    `${logisticsRisks.length} problema(s) logístico(s) detectado(s)${blocked.length > 0 ? `, ${blocked.length} rota(s) bloqueada(s)` : ''}.\n\nVerifique rotas alternativas e redistribua o fluxo de materiais.`,
    0.82,
    [],
  )
}

function handleSimulacao(state: TwinState, question: string): TwinCopilotResponse {
  return respond(
    'simulacao',
    'Para simular cenários operacionais, acesse a aba "Simulação" no Digital Twin. Posso analisar: atraso de concretagem, redução de efetivo, ruptura de supply, clima extremo e falha de equipamento.',
    0.75,
    [],
  )
}

function handleResumo(state: TwinState): TwinCopilotResponse {
  const criticalRisks = state.risks.filter(r => r.is_active && (r.severity === 'critical' || r.severity === 'emergency'))
  const criticalZones = state.zones.filter(z => z.risk_level === 'critical').length
  const criticalEquip = state.equipment.filter(e => e.status === 'critical' || e.status === 'offline').length
  const activeCorrelations = state.correlations.filter(c => c.is_active).length

  const healthEmoji = criticalRisks.length === 0 ? '🟢' : criticalRisks.length < 3 ? '🟡' : '🔴'

  const summary = [
    `${healthEmoji} Digital Twin — ${state.zones.length} zonas monitoradas`,
    `• ${criticalRisks.length} risco(s) crítico(s) ativo(s)`,
    `• ${criticalZones} zona(s) em estado crítico`,
    `• ${criticalEquip} equipamento(s) com problema`,
    activeCorrelations > 0 ? `• ${activeCorrelations} correlação(ões) de risco detectada(s)` : '',
  ].filter(Boolean).join('\n')

  return respond('resumo', summary, 0.9, [])
}

// ─── Helpers ─────────────────────────────────────────────────
function respond(
  intent:          string,
  answer:          string,
  confidence:      number,
  recommendations: OperationalRecommendation[],
  zones?:          string[],
): TwinCopilotResponse {
  return { answer, intent, confidence, recommendations, zones }
}

function buildZoneRec(
  zone:  ObraZone,
  risks: SpatialRisk[],
): OperationalRecommendation {
  const topRisk = risks[0]
  return {
    id:         `rec_zone_${zone.id}_${Date.now()}`,
    category:   topRisk?.risk_type === 'sst' ? 'safety' : 'production',
    priority:   zone.risk_level === 'critical' ? 1 : 2,
    title:      `Inspecionar zona: ${zone.nome}`,
    rationale:  topRisk?.title ?? `Zona com risco ${zone.risk_level}`,
    action:     `Enviar responsável para vistoria imediata de ${zone.nome}`,
    impact:     'Identificação e mitigação de risco antes de escalonamento',
    confidence: 0.82,
    timeframe:  zone.risk_level === 'critical' ? 'immediate' : 'today',
    zone_id:    zone.id,
    expires_at: new Date(Date.now() + 3_600_000).toISOString(),
  }
}
